const VIDEO_ID_PATTERN = '[A-Za-z0-9_-]{11}';
const IFRAME_PATTERN = /<iframe\b[\s\S]*?<\/iframe>/gi;
const WRAPPER_CLASS_PATTERN = /\b(?:video-shell|iframe-shell)\b/i;
const VIDEO_SHELL_CLASS_PATTERN = /\bvideo-shell\b/i;

function getIframeSrc(iframe: string): string | null {
  const match = iframe.match(/\bsrc\s*=\s*(["'])([\s\S]*?)\1/i);
  return match ? match[2].replace(/&amp;/g, '&') : null;
}

export function extractYoutubeVideoId(src: string): string | null {
  if (!src) {
    return null;
  }

  const embed = src.match(
    new RegExp(`youtube(?:-nocookie)?\\.com/embed/(${VIDEO_ID_PATTERN})`, 'i')
  );
  if (embed) {
    return embed[1];
  }

  const short = src.match(new RegExp(`youtu\\.be/(${VIDEO_ID_PATTERN})`, 'i'));
  if (short) {
    return short[1];
  }

  if (/youtube(?:-nocookie)?\.com/i.test(src)) {
    const watch = src.match(new RegExp(`[?&]v=(${VIDEO_ID_PATTERN})`, 'i'));
    if (watch) {
      return watch[1];
    }
  }

  return null;
}

function escapeAttribute(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;');
}

function fallbackLink(videoId: string): string {
  return `<p class="youtube-fallback"><a href="https://www.youtube.com/watch?v=${videoId}" target="_blank" rel="noopener noreferrer">Watch on YouTube</a></p>`;
}

function nocookieIframe(videoId: string, originalIframe: string): string {
  const titleMatch = originalIframe.match(/\btitle\s*=\s*(["'])([\s\S]*?)\1/i);
  const title = titleMatch
    ? ` title="${escapeAttribute(titleMatch[2])}"`
    : '';
  const allowfullscreen = /\ballowfullscreen\b/i.test(originalIframe)
    ? ' allowfullscreen'
    : '';

  return `<iframe src="https://www.youtube-nocookie.com/embed/${videoId}"${title}${allowfullscreen}></iframe>`;
}

function findWrapper(html: string, iframeStart: number, iframeEnd: number): {
  start: number;
  end: number;
  openTag: string;
  isVideoShell: boolean;
} | null {
  const before = html.slice(0, iframeStart);
  const after = html.slice(iframeEnd);
  const openMatch = before.match(
    /<(div|figure)([^>]*\bclass\s*=\s*["'][^"']*["'][^>]*)>\s*$/i
  );
  if (!openMatch || !WRAPPER_CLASS_PATTERN.test(openMatch[2])) {
    return null;
  }

  const closeMatch = after.match(new RegExp(`^\\s*</${openMatch[1]}>`, 'i'));
  if (!closeMatch) {
    return null;
  }

  return {
    start: iframeStart - openMatch[0].length,
    end: iframeEnd + closeMatch[0].length,
    openTag: openMatch[0],
    isVideoShell: VIDEO_SHELL_CLASS_PATTERN.test(openMatch[2]),
  };
}

export function rewriteYoutubeEmbeds(html: string, hasConsent: boolean): string {
  if (!html) {
    return html;
  }

  const replacements: { start: number; end: number; text: string }[] = [];
  const iframeRe = new RegExp(IFRAME_PATTERN.source, IFRAME_PATTERN.flags);
  let match: RegExpExecArray | null;

  while ((match = iframeRe.exec(html)) !== null) {
    const iframe = match[0];
    const src = getIframeSrc(iframe);
    if (!src) {
      continue;
    }

    const videoId = extractYoutubeVideoId(src);
    if (!videoId) {
      continue;
    }

    const iframeStart = match.index;
    const iframeEnd = match.index + iframe.length;
    const wrapper = findWrapper(html, iframeStart, iframeEnd);

    let text: string;
    if (!hasConsent) {
      text = fallbackLink(videoId);
    } else {
      const rewrittenIframe = nocookieIframe(videoId, iframe);
      if (wrapper?.isVideoShell) {
        text = `${wrapper.openTag}${rewrittenIframe}${html.slice(iframeEnd, wrapper.end)}`;
      } else {
        text = `<div class="video-shell">${rewrittenIframe}</div>`;
      }
    }

    replacements.push({
      start: wrapper ? wrapper.start : iframeStart,
      end: wrapper ? wrapper.end : iframeEnd,
      text,
    });
  }

  let result = html;
  for (let i = replacements.length - 1; i >= 0; i--) {
    const replacement = replacements[i];
    result =
      result.slice(0, replacement.start) +
      replacement.text +
      result.slice(replacement.end);
  }

  return result;
}
